import { InjectionToken, NgModule } from '@angular/core';
import { APOLLO_NAMED_OPTIONS, APOLLO_OPTIONS } from 'apollo-angular';
import { ApolloClientOptions, ApolloLink, InMemoryCache, split } from '@apollo/client/core';
import { HttpLink } from 'apollo-angular/http';
import { setContext } from '@apollo/client/link/context';
import { WebSocketLink } from '@apollo/client/link/ws';
import { onError } from '@apollo/client/link/error';
import { makeStateKey, TransferState } from '@angular/platform-browser';
import { environment } from "../../src/environments/environment"
import { LoginService } from './core/services/login.service';
import { getMainDefinition } from '@apollo/client/utilities';
import { SubscriptionClient } from 'subscriptions-transport-ws';
export let subscriptionClient: SubscriptionClient = null;
const APOLLO_CACHE = new InjectionToken<InMemoryCache>('apollo-cache');
const STATE_KEY = makeStateKey<any>('apollo-state');
export function createDefaultApollo(httpLink: HttpLink,
	cache: InMemoryCache,
	transferState: TransferState): ApolloClientOptions<any> {
	const isBrowser = transferState.hasKey<any>(STATE_KEY);
	if (isBrowser) {
		const state = transferState.get<any>(STATE_KEY, null);
		cache.restore(state);
	} else {
		transferState.onSerialize(STATE_KEY, () => {
			return cache.extract();
		});
	}

	const basic = setContext((operation, context) => ({
		headers: {
			Accept: 'charset=utf-8'
		}
	}));

	const auth = setContext((operation, context) => {
		return {
			headers: {
				'x-hasura-admin-secret': 'ZfEL0n3LsZMGB0oRJCdToQQhMhALea6s0i2VwzhvW76XuDxuwGc9A25kRLDghYwO'
			}
		};
	});
	const link = ApolloLink.from([basic, auth, httpLink.create({ uri: environment.hasuraInternalUrl })]);

	return {
		link,
		cache,
	};
}

export function createNamedApollo(httpLink: HttpLink,
	cache: InMemoryCache,
	transferState: TransferState,
	loginService: LoginService
): Record<string, ApolloClientOptions<any>> {

	const isBrowser = transferState.hasKey<any>(STATE_KEY);
	if (isBrowser) {
		const state = transferState.get<any>(STATE_KEY, null);
		cache.restore(state);
	} else {
		transferState.onSerialize(STATE_KEY, () => {
			return cache.extract();
		});
	}

	const authLink = setContext((operation, context) => {
		const loginData = loginService.getLoginDataDirectory();
		//console.log(loginData);
		if (loginData) {
			const header = { 'Authorization': `Bearer ${loginData.accessToken}` };
			return { headers: header };
		} else {
			return null;
		}
	});

	const errorLink = onError(({ graphQLErrors, networkError }) => {
		//console.log(graphQLErrors, networkError);
		if (graphQLErrors) {
			graphQLErrors.forEach(({ message, locations, path }) =>
				console.log(
					`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
				)
			);
		}
		if (networkError) {
			//console.log(JSON.stringify(networkError));
			const error: any = networkError;
			if (error && error.extensions && error.extensions.code === 'start-failed') {
				loginService.removeLoginData();
				// subscription.close();
				location.replace('/');
			}
		}
	});

	const http = httpLink.create({ uri: environment.hasuraExternalHttpUrl });
	// Create a WebSocket link:
	const ws = new WebSocketLink({
		uri: environment.hasuraExternalWssUrl,
		options: {
			lazy: true,
			reconnect: true,
			reconnectionAttempts: 5,
			connectionParams: () => {
				const loginData = loginService.getLoginDataDirectory();
				if (loginData) {
					const header = { 'Authorization': `Bearer ${loginData.accessToken}` };
					return {
						headers: header
					}
				} else {
					return null;
				}
			}
		},
	});

	subscriptionClient = (<any>ws).subscriptionClient;
	const link = split(
		// split based on operation type
		({ query }) => {
			const { kind, operation } = getMainDefinition(query) as any;
			return (
				kind === 'OperationDefinition' && operation === 'subscription'
			);
		},
		errorLink.concat(authLink).concat(ws),
		errorLink.concat(authLink).concat(http),
	);

	return {
		external: {
			link,
			cache,
			defaultOptions: {
				watchQuery: {
					fetchPolicy: 'no-cache',
					errorPolicy: 'ignore',
				},
				query: {
					fetchPolicy: 'no-cache',
					errorPolicy: 'all',
				},
			}
		}
	}
}

@NgModule({
	providers: [
		{
			provide: APOLLO_CACHE,
			useValue: new InMemoryCache(),
		},
		{
			provide: APOLLO_OPTIONS,
			useFactory: createDefaultApollo,
			deps: [HttpLink, APOLLO_CACHE, TransferState],
		},
		{
			provide: APOLLO_NAMED_OPTIONS,
			useFactory: createNamedApollo,
			deps: [HttpLink, APOLLO_CACHE, TransferState, LoginService],
		},
	],
})
export class GraphQLModule { }
